import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useContent from "@/lib/useContent";
import { PACKAGES } from "@/lib/site-data";
import { JOURNEYS } from "@/lib/packages-data";
import { scrollToId } from "@/lib/useLenis";

const EASE = [0.76, 0, 0.24, 1];
const HEADER_VIEWPORT = { once: true, margin: "-80px" };
const CARD_VIEWPORT = { once: true, margin: "-100px" };
const REVEAL_INITIAL = { opacity: 0, y: 40 };
const REVEAL_VISIBLE = { opacity: 1, y: 0 };
const HEADER_TRANSITION = { duration: 1, ease: EASE };
const CARD_INITIAL = { opacity: 0, y: 60 };
const CARD_TRANSITION = { duration: 0.95, ease: EASE };
const IMG_HOVER = { scale: 1.05 };
const IMG_TRANSITION = { duration: 1.2, ease: EASE };
const CTA_TRANSITION = { duration: 0.9, ease: EASE };

export default function Packages({ onSelect }) {
  const journeys = useContent("journeys", JOURNEYS);
  const cards = PACKAGES.map((p) => {
    const live = p.slug && journeys.find((x) => x.slug === p.slug);
    return live
      ? { ...p, title: live.title, subtitle: live.subtitle, duration: live.duration, from: live.from }
      : p;
  });

  const handlePick = (pkg) => {
    if (onSelect) onSelect(pkg.title);
    scrollToId("contact");
  };

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-24 md:py-40 bg-background"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <PackagesHeader />
        <div className="space-y-24 md:space-y-32">
          {cards.map((p, i) => (
            <PackageCard key={p.id} p={p} reverse={i % 2 === 1} onPick={handlePick} />
          ))}
        </div>
        <ViewAllJourneys count={journeys.length} />
      </div>
    </section>
  );
}

function PackagesHeader() {
  return (
    <div className="grid md:grid-cols-[220px_1fr_auto] gap-8 md:gap-16 mb-16 md:mb-24 items-end">
      <div className="text-[11px] uppercase tracking-[0.3em] text-foreground/60">
        <div className="flex items-center gap-3">
          <span className="w-6 h-px bg-primary" />
          <span>Ch. 03 · Journeys</span>
        </div>
      </div>

      <motion.h2
        initial={REVEAL_INITIAL}
        whileInView={REVEAL_VISIBLE}
        viewport={HEADER_VIEWPORT}
        transition={HEADER_TRANSITION}
        className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.94] tracking-tighter"
      >
        Four <em className="italic text-primary">signature</em> ways in.
      </motion.h2>

      <div className="text-sm md:text-base text-foreground/60 max-w-xs">
        Our four favourite starting points — with the full catalogue of
        journeys just below. Every one is private and endlessly
        editable.
      </div>
    </div>
  );
}

function PackageCard({ p, reverse, onPick }) {
  return (
    <motion.article
      initial={CARD_INITIAL}
      whileInView={REVEAL_VISIBLE}
      viewport={CARD_VIEWPORT}
      transition={CARD_TRANSITION}
      className="group grid md:grid-cols-12 gap-8 md:gap-12 items-center"
      data-testid={`package-card-${p.id}`}
    >
      <div className={`md:col-span-5 ${reverse ? "md:order-2 md:col-start-8" : ""}`}>
        <div className="relative overflow-hidden aspect-[3/4] md:aspect-[4/5] bg-muted">
          <motion.img
            src={p.img}
            alt={p.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover duotone"
            whileHover={IMG_HOVER}
            transition={IMG_TRANSITION}
          />
          <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-white/90 mix-blend-difference">
            {p.number} / 04
          </div>
        </div>
      </div>

      <div className={`md:col-span-6 ${reverse ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
        <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">
          {p.subtitle}
        </div>
        <h3 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tight">
          {p.title}
        </h3>
        <p className="mt-6 max-w-lg text-base md:text-lg font-light text-foreground/75 leading-relaxed">
          {p.body}
        </p>

        <div className="mt-8 flex flex-wrap gap-2">
          {p.tags.map((t) => (
            <span
              key={t}
              className="text-[10px] uppercase tracking-[0.22em] border border-foreground/25 px-3 py-1.5 text-foreground/70"
            >
              {t}
            </span>
          ))}
        </div>

        <PackageCardFooter p={p} onPick={onPick} />
      </div>
    </motion.article>
  );
}

function PackageCardFooter({ p, onPick }) {
  return (
    <div className="mt-10 flex items-end justify-between gap-6 border-t border-foreground/15 pt-6">
      <div className="flex gap-8 md:gap-12">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-foreground/55">
            Duration
          </div>
          <div className="font-serif text-xl md:text-2xl mt-1">{p.duration}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-foreground/55">
            From
          </div>
          <div className="font-serif text-xl md:text-2xl mt-1">{p.from}</div>
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        {p.slug && (
          <Link
            to={`/journeys/${p.slug}`}
            data-testid={`package-detail-link-${p.id}`}
            className="btn-sharp inline-flex items-center gap-3 border border-primary bg-primary text-primary-foreground px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-medium"
          >
            Full itinerary
            <span aria-hidden>→</span>
          </Link>
        )}
        <button
          onClick={() => onPick(p)}
          data-testid={`package-cta-${p.id}`}
          className="btn-sharp inline-flex items-center gap-3 border border-foreground px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-medium"
        >
          Enquire
          <span aria-hidden>→</span>
        </button>
      </div>
    </div>
  );
}

function ViewAllJourneys({ count }) {
  return (
    <motion.div
      initial={REVEAL_INITIAL}
      whileInView={REVEAL_VISIBLE}
      viewport={HEADER_VIEWPORT}
      transition={CTA_TRANSITION}
      className="mt-24 md:mt-32 border-t border-foreground/15 pt-10"
    >
      <Link
        to="/journeys"
        data-testid="view-all-journeys"
        className="group flex items-end justify-between gap-6"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/55 mb-3">
            The full catalogue
          </div>
          <div className="font-serif font-light text-4xl md:text-6xl tracking-tighter group-hover:text-primary transition-colors">
            View all <em className="italic text-primary">{count}</em> journeys
          </div>
        </div>
        <span aria-hidden className="text-3xl md:text-5xl transition-transform duration-500 group-hover:translate-x-3">
          →
        </span>
      </Link>
    </motion.div>
  );
}
